import { pieceIndexAt, type TimelinePiece } from '../../utils/mediaTimeline'
import { itemFramePieces } from '../sceneWindow'
import type { TimelineAsset, TimelineItem } from '../types'

type MediaItem = Extract<TimelineItem, { type: 'video' | 'audio' }>

/** A bound media item as a piece of the visualizer's timeline, with its asset. */
export type VisualizerPiece = TimelinePiece & { assetId: string }

/**
 * The visualizer's bound items as pieces on its own timeline (it starts at
 * `from`), each keeping the asset it plays, so a track that cuts between
 * recordings is analyzed from whichever one is playing. A scene with cuts in
 * its window is one piece per kept part (#77), so the analysis follows the
 * cuts the audio plays around.
 */
export function visualizerPieces({
  mediaItems,
  from,
  fps,
  assets = {},
}: {
  mediaItems: MediaItem[]
  from: number
  fps: number
  /** Where each scene's cuts are read from; none without it. */
  assets?: Readonly<Record<string, TimelineAsset>>
}): VisualizerPiece[] {
  return mediaItems.flatMap((media) =>
    itemFramePieces(media, assets, fps).map((piece) => ({
      ...piece,
      assetId: media.assetId,
      from: media.from - from + piece.from,
    })),
  )
}

/**
 * The asset playing at `frame` of the visualizer's timeline, or none while
 * the bound track is silent (before its first scene, between two, after its
 * last): the visualizer shows nothing then, as captions show no words.
 */
export function assetAt(
  pieces: readonly VisualizerPiece[],
  frame: number,
): string | undefined {
  const piece = pieces[pieceIndexAt(pieces, frame)]
  if (!piece) return undefined
  if (frame < piece.from || frame >= piece.from + piece.durationInFrames)
    return undefined
  return piece.assetId
}
